"use client";

import React from "react";
import Link from "next/link";
import { formatDate } from "@/utils/commonFunctions";
import Icon from "@/components/icons/Icons";
import AskQuestionButton from "./AskQuestionButton";

export interface MoreQuestionItem {
  id: number;
  question: string | null;
  txttitle: string | null;
  pagename: string | null;
  date: string | null;
  answer_count: number | null;
  permalinks: string | null;
}

interface MoreQuestionsSidebarProps {
  questions: MoreQuestionItem[];
}

export default function MoreQuestionsSidebar({
  questions,
}: MoreQuestionsSidebarProps) {
  return (
    <aside className="space-y-6">
      {/* More Questions Card */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-xs">
        <div className="flex items-center justify-between pb-3.5 mb-4 border-b border-slate-100">
          <h3 className="text-base sm:text-lg font-bold text-slate-900">
            More Questions
          </h3>
          <Link
            href="/questions"
            className="text-xs font-semibold text-primaryBlue hover:underline inline-flex items-center gap-1"
          >
            View all
            <Icon.rightArrow className="w-2.5 h-2.5" />
          </Link>
        </div>

        {questions && questions.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {questions.map((q) => {
              const questionTitle = q.question || q.txttitle || "Question";
              const href = `/questions/${q.permalinks || q.id}`;
              const count = q.answer_count || 0;
              const formattedDate = q.date ? formatDate(q.date) : null;

              return (
                <div key={q.id} className="py-3 first:pt-0 last:pb-0 group">
                  <Link
                    href={href}
                    className="block group-hover:text-primaryBlue transition-colors"
                  >
                    <h4
                      title={questionTitle}
                      className="text-xs sm:text-sm sm:leading-5 font-semibold text-slate-800 group-hover:text-primaryBlue transition-colors line-clamp-2 mb-1.5 leading-snug"
                    >
                      {questionTitle}
                    </h4>
                    <div className="flex items-center gap-1.5 text-2xs text-slate-500">
                      <span>
                        {count} {count === 1 ? "answer" : "answers"}
                      </span>
                      {formattedDate && (
                        <>
                          <span className="text-slate-300">•</span>
                          <span>{formattedDate}</span>
                        </>
                      )}
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-xs text-slate-400 py-2">No other questions found.</p>
        )}
      </div>

      {/* Ask Community Box */}
      <div className="bg-[linear-gradient(to_bottom_right,#eff6ff,#f8fafc)] border border-blue-100 rounded-2xl p-5 sm:p-6 shadow-xs">
        <div className="w-10 h-10 rounded-full bg-blue-100 text-primaryBlue flex items-center justify-center mb-3 font-bold text-lg">
          ?
        </div>
        <h3 className="text-sm sm:text-base font-bold text-slate-900 mb-1">
          Have a Question?
        </h3>
        <p className="text-xs text-slate-500 mb-4 leading-relaxed">
          Ask our community of mutual fund investors and financial experts for guidance.
        </p>
        <AskQuestionButton
          className="inline-flex items-center justify-center w-full px-4 py-2.5 rounded-xl bg-primaryBlue hover:bg-primaryBlue/90 text-white font-semibold text-xs sm:text-sm transition shadow-xs cursor-pointer"
          buttonText="+ Ask a Question"
        />
      </div>
    </aside>
  );
}
